/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  FormErrorMessage,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  Button,
  ModalFooter,
  useToast,
  Select,
  Center,
  Stack,
  HStack,
  Checkbox,
  Switch,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';

import Schemas, { GEOFENCED } from '../api/schemas';
import { firebaseFacade } from '../api/firebase';
import { log, MISTERY_WALLET } from '../utils';
import { indexer } from '../api/indexing';
import { configContext } from '../api/config';

export function ArtPreview({ url, onDelete }) {
  return (
    <HStack>
      <Image width={75} height={75} src={url} alt="Queue art" />
      <Button onClick={onDelete} size="xs" ml="20px" bg={'transparent'}>
        <span class="material-icons">delete</span>
      </Button>
    </HStack>
  );
}

export default function QueueCreateModal({ isOpen, onClose }) {
  const queueNameRef = useRef();
  const queueArtRef = useRef();

  const subscriptionScheduleRef = useRef();

  const [name, setName] = useState(null);
  const [price, setPrice] = useState(0);
  const [priceError, setPriceError] = useState(false);

  const toast = useToast();
  const { publicKey } = useWallet();
  const [errorMessage, setErrorMessage] = useState();

  const { queue_schema, subscription_schema } = Schemas;
  const [loading, setLoading] = useState(false);
  const [art, setArt] = useState();

  const [artUri, setArtUri] = useState();
  const [subscribable, setSubscribable] = useState(false);
  const [locked, setLocked] = useState(true);

  const [queueType, setQueueType] = useState(0);
  const [geoRef, setGeoRef] = useState(null);
  const [isNavigatorAvailable, setNavigatorAvailable] = useState(false);

  const [radius, setRadius] = useState(null);
  const [radiusError, setRadiusError] = useState(null);

  const handleAddSubs = ({ target: { checked } }) => {
    setSubscribable(checked);
  };

  const handleQueueTypeSelect = ({ target: { value } }) => {
    log({ component: 'QueueCreateModal', queueType: value });
    setQueueType(Number.parseInt(value));
  };

  const handleRadiusChange = ({ target: { value } }) => {
    log({ component: 'QueueCreateModal', radius: value });
    if (value === '') {
      setRadius(null);
    } else {
      setRadius(Number.parseInt(value));
      setRadiusError(null);
    }
  };

  const handleQueueNameChange = ({ target: { value: name } }) => {
    if (name.length > 26) {
      setErrorMessage(
        "Please enter a name for your Queue that's 26 characters or less"
      );
      return;
    }

    setName(name);
    setErrorMessage(null);
  };

  const onCreate = async () => {
    if (errorMessage || priceError || radiusError) return;
    if (name === null || name === '') {
      setErrorMessage('Queue name cannot be empty');
      return;
    }

    if (queueType === GEOFENCED && (radius === null || radius === '')) {
      setRadiusError('Radius is required for Geo-Fenced queues');
      return;
    }

    setLoading(true);
    const queue = { ...queue_schema };
    if (publicKey === null) {
      toast({
        title: 'Orphaned Queue',
        description:
          'Orphaned queues are deleted after 48hrs. Browse with a Web3 browser to avoid this.',
        duration: 5000,
        isClosable: true,
        status: 'warning',
        position: 'top',
      });
    }

    const queueRef = firebaseFacade.createQueueRef();
    queue.name = name;
    queue.owner_wallet_address =
      (publicKey && publicKey.toBase58()) || MISTERY_WALLET;
    queue.queue_address = queueRef.id;

    queue.number_of_hours = 0;
    queue.number_of_tracks = 0;
    queue.collabs = 0;

    queue.type = queueType;
    queue.created_on = Date.now();
    queue.locked = locked && publicKey !== null;

    queue.geo_reference = geoRef;
    queue.radius = radius;

    if (subscribable) {
      const {
        current: { value: schedule },
      } = subscriptionScheduleRef;
      const subscription = { ...subscription_schema };

      subscription.schedule = Number.parseInt(schedule);
      subscription.price = price;
      queue.subscription = subscription;
    } else {
      queue.subscription = null;
    }

    log({ component: 'QueueCreateModal', queue: queue });
    try {
      queue.art_uri =
        (art && (await firebaseFacade.uploadArt(queueRef.id, art))) ||
        `${process.env.PUBLIC_URL}/logo-512x512-bg.png`;
      await firebaseFacade.insertDocument(queueRef, queue);
      setTimeout(
        () =>
          toast({
            title: queue.name,
            description:
              'Hooray! Your queue was created successfully. Now add some original tracks!',
            duration: 5000,
            isClosable: true,
            status: 'success',
            position: 'top',
          }),
        publicKey === null ? 500 : 0
      );
      indexer.indexQueue(queue);
    } catch (error) {
      log({ component: 'QueueCreateModal', error: error });
      toast({
        title: queue.name,
        description:
          'OhOh! Your queue was not created successfully. Please try again!',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }
    setLoading(false);
    onClose();
  };

  const handleRemove = () => {
    URL.revokeObjectURL(artUri);
    setArt(null);
    setArtUri(null);
    queueArtRef.current.value = null;
  };

  const handleArtSelect = ({
    target: {
      files: [art],
    },
  }) => {
    art && handleRemove();
    setArtUri(URL.createObjectURL(art));
    setArt(art);
  };

  const handleArtSelectClick = () => {
    queueArtRef.current.click();
  };

  const handlePriceChange = ({ target: { value } }) => {
    const price = Number.parseFloat(value);
    setPrice(price);
    setPriceError(price !== 0 && price < 1e-8);
  };

  const handleLock = ({ target: { checked } }) => {
    setLocked(checked);
  };

  const isGeofenceEnabled = () => {
    return configContext
      .fetchValue(configContext.configKeys.geofenceQueueEnabled)
      .asBoolean();
  };

  useEffect(() => {
    if (queueType === GEOFENCED) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        position => {
          const { coords } = position;
          setGeoRef({
            latitude: coords.latitude,
            longitude: coords.longitude,
            altitude: coords.altitude,
          });
          setLoading(false);
        },
        error => {
          const { code } = error;
          setLoading(false);
          if (code === 1) {
            toast({
              title: 'Enable Location Access',
              description:
                'OhOh! Location services is needed for Geo Fenced queues',
              duration: 5000,
              isClosable: true,
              status: 'info',
              position: 'top',
            });
          } else {
            toast({
              title: 'Location Access',
              description:
                'OhOh! Failed to get your position. Please try again',
              duration: 5000,
              isClosable: true,
              status: 'info',
              position: 'top',
            });
            setQueueType(0);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
        }
      );
    }
  }, [queueType]);

  useEffect(() => {
    setNavigatorAvailable('geolocation' in navigator);
  }, []);

  return (
    <Modal initialFocusRef={queueNameRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Queue</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing={5}>
            <FormControl isInvalid={errorMessage}>
              <FormLabel>Name</FormLabel>
              <Input
                ref={queueNameRef}
                onChange={handleQueueNameChange}
                placeholder="Enter Queue Name"
              />
              <FormErrorMessage>{errorMessage}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <Select
                onChange={handleQueueTypeSelect}
                placeholder="Select type"
                defaultValue={queueType}
                value={queueType}
              >
                <option value={0}>Public</option>
                {isNavigatorAvailable && isGeofenceEnabled() && (
                  <option value={2}>Geo Fenced</option>
                )}
                {publicKey && <option value={1}>Private</option>}
              </Select>
            </FormControl>
            {queueType === GEOFENCED && (
              <FormControl isInvalid={radiusError}>
                <FormLabel>Radius(miles)</FormLabel>
                <Input
                  type={'number'}
                  onChange={handleRadiusChange}
                  placeholder="Enter visibility radius"
                />
                <FormErrorMessage>{radiusError}</FormErrorMessage>
              </FormControl>
            )}
            {publicKey && (
              <>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="locked" mb="0">
                    {locked ? 'Unlock' : 'Lock'}
                  </FormLabel>
                  <Switch
                    defaultChecked
                    id="locked"
                    colorScheme={'yellow'}
                    onChange={handleLock}
                  />
                </FormControl>
                <Checkbox onChange={handleAddSubs}>Add subscription</Checkbox>
                {subscribable && (
                  <>
                    <FormControl isInvalid={priceError}>
                      <FormLabel>Subscription Price (QDOT)</FormLabel>
                      <Input type={'number'} onChange={handlePriceChange} />
                      {priceError && (
                        <FormErrorMessage>
                          {'Price must be 0 or >= 1E-8'}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                    <FormControl>
                      <FormLabel>Subscription Schedule</FormLabel>
                      <Select
                        ref={subscriptionScheduleRef}
                        placeholder="Select Schedule"
                        defaultValue={0}
                      >
                        <option value={0}>Monthly</option>
                        <option value={1}>Yearly</option>
                      </Select>
                    </FormControl>
                  </>
                )}
              </>
            )}
            <FormControl>
              <Center>
                <Input
                  ref={queueArtRef}
                  type={'file'}
                  accept="image/*"
                  display={'none'}
                  onChange={handleArtSelect}
                />
                <Button onClick={handleArtSelectClick}>Upload Queue Art</Button>
              </Center>
            </FormControl>
            {artUri && <ArtPreview url={artUri} onDelete={handleRemove} />}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            onClick={onCreate}
            bg={'transparent'}
            mr={3}
          >
            Create
          </Button>
          <Button bg={'transparent'} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
